/**
 * Internal FLEET-DEFAULT Feature-Config API Contract (SCH-1686)
 *
 * PATCH /internal/config/global-features/:feature
 * PATCH /internal/config/screen-features/:screen
 *
 * The system-row siblings of the per-school verbs in `school-config.ts`. Those
 * write ONE school's override; these write the `SystemConfiguration` row with
 * `school_id IS NULL` — **the default every school without its own override
 * inherits**. Same key gate, same sparse-patch body, one tenant versus the whole
 * fleet.
 *
 * WHY THEY EXIST, given SCH-1665 already shipped the school half. The
 * fleet-default row had no agent-callable writer at all, and the super-admin
 * editor only renders features the row ALREADY carries — so a feature missing
 * from it could not be added from any UI, by anyone. `attendance` was exactly
 * that in production: absent from the fleet default, therefore unaddable, while
 * every school inherited nothing for it.
 *
 * THE ONE RULE THAT DIFFERS FROM THE SCHOOL VERBS. A school override row is
 * SPARSE by design — the system row supplies the base fields underneath it — so
 * any subset of a feature's keys is a valid school patch. The system row is that
 * bottom: nothing merges underneath it, and the tolerant read DROPS an entry
 * that does not parse, so a half-formed fleet default would take the feature
 * dark for every school on the next read. So:
 *
 *   - Patching a feature the row ALREADY carries is fully sparse. `{"autoSync":
 *     true}` keeps `enabled`, `visibleForRoles` and every sibling.
 *   - A patch that CREATES a feature must carry that feature's required fields —
 *     `enabled` and `visibleForRoles`. There is nothing to merge them from, and
 *     defaulting them would mean inventing a fleet-wide visibility policy the
 *     caller never asked for. The 400 names the missing path, e.g.
 *     `attendance.visibleForRoles`.
 *
 * Feature-specific validation is deliberately NOT repeated here, for the reason
 * `school-config.ts` gives: the config services already validate the touched
 * block against its own schema, and a second copy of those rules in the wire
 * contract would be a second thing to keep in step.
 */

import { z } from 'zod'
import { SchoolConfigPatchSchema } from './school-config'

/**
 * The request body for BOTH verbs: a non-empty sparse patch object.
 *
 * Deliberately the SAME schema object as the per-school verbs rather than a
 * copy. The body shape is the contract's one genuinely shared half — a non-empty
 * object, arrays and `null` refused — and two declarations of it would be two
 * things to keep in step for no gain. The scope difference lives in the ROUTE,
 * which is where an operator reads it.
 */
export const FleetConfigPatchSchema = SchoolConfigPatchSchema
export type FleetConfigPatch = z.infer<typeof FleetConfigPatchSchema>

/**
 * Response for both verbs — the fleet-default block AS STORED after the write,
 * re-read from the primary past every cache rather than echoed from the request.
 *
 * The system row IS the whole answer here, so unlike the school response there
 * is no merged-versus-raw distinction to make: what this returns is both what
 * was written and what every school now inherits.
 *
 * `scope` is a constant, and it is here on purpose. These responses are read out
 * of a terminal beside the near-identical per-school ones, and an operator who
 * has just run eight school patches needs the answer to say, in the payload
 * itself, that this one was not one of them.
 */
const FleetConfigWriteResponseBase = {
  scope: z
    .literal('fleet-default')
    .describe('Always `fleet-default` — this wrote the system row every school inherits, not one school'),
  config: z
    .record(z.string(), z.unknown())
    .nullable()
    .describe('The stored fleet-default block after the write (fresh primary read); null when the row defines none')
}

/** `PATCH /internal/config/global-features/:feature`. */
export const SetFleetGlobalFeatureResponseSchema = z.object({
  ...FleetConfigWriteResponseBase,
  feature: z.string().describe('The global-feature key that was patched')
})
export type SetFleetGlobalFeatureResponse = z.infer<typeof SetFleetGlobalFeatureResponseSchema>

/** `PATCH /internal/config/screen-features/:screen`. */
export const SetFleetScreenFeaturesResponseSchema = z.object({
  ...FleetConfigWriteResponseBase,
  screen: z.string().describe('The screen key that was patched')
})
export type SetFleetScreenFeaturesResponse = z.infer<typeof SetFleetScreenFeaturesResponseSchema>
